import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  IconButton,
  Stack,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NotesIcon from "@mui/icons-material/Notes";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import api from "../../utils/api";

const formatDate = (d) => (d ? new Date(d).toLocaleString() : "N/A");

const OwnerNotesTab = ({ memberCode }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const loadNotes = useCallback(async () => {
    if (!memberCode) return;

    try {
      setLoading(true);
      const res = await api.get(`/members/${memberCode}/owner-notes`);
      setNotes(res.data.notes || []);
    } catch (error) {
      console.error("Failed to load owner notes:", error);
      showSnackbar("Failed to load owner notes.", "error");
    } finally {
      setLoading(false);
    }
  }, [memberCode]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const addNote = async () => {
    if (!newNote.trim()) {
      showSnackbar("Please enter a note before adding.", "warning");
      return;
    }

    try {
      setSaving(true);
      await api.post(`/members/${memberCode}/owner-notes`, {
        note_text: newNote,
        created_by: localStorage.getItem("username") || "Unknown",
      });

      setNewNote("");
      await loadNotes();
      showSnackbar("Note added successfully.");
    } catch (error) {
      console.error("Failed to add note:", error);
      showSnackbar("Failed to add note.", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateNote = async (id) => {
    if (!editText.trim()) {
      showSnackbar("Note text cannot be empty.", "warning");
      return;
    }

    try {
      setSaving(true);
      await api.put(`/owner-notes/${id}`, {
        note_text: editText,
        updated_by: localStorage.getItem("username") || "Unknown",
      });

      setEditId(null);
      setEditText("");
      await loadNotes();
      showSnackbar("Note updated successfully.");
    } catch (error) {
      console.error("Failed to update note:", error);
      showSnackbar("Failed to update note.", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteNote = async (id) => {
    try {
      await api.delete(`/owner-notes/${id}`);
      await loadNotes();
      showSnackbar("Note deleted successfully.");
    } catch (error) {
      console.error("Failed to delete note:", error);
      showSnackbar("Failed to delete note.", "error");
    }
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditText("");
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: "#223142",
            mb: 0.5,
          }}
        >
          Owner Notes
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b778c",
          }}
        >
          Add, review, and manage notes related to this member account.
        </Typography>
      </Box>

      <Card sx={{ ...pageCardSx, p: { xs: 1.5, sm: 2, md: 2.5 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(47,99,173,0.08)",
            }}
          >
            <NotesIcon sx={{ color: "#2f63ad" }} />
          </Box>

          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", lineHeight: 1.2 }}
            >
              Add Note
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Create a new owner note for this member.
            </Typography>
          </Box>
        </Box>

        <Stack spacing={2}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Owner Note"
            placeholder="Enter note details..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={textFieldSx}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addNote}
              disabled={saving}
              sx={primaryButtonSx}
            >
              {saving ? "Adding..." : "Add Note"}
            </Button>
          </Box>
        </Stack>
      </Card>

      {loading ? (
        <Box
          sx={{
            minHeight: "180px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : notes.length === 0 ? (
        <Card sx={{ ...pageCardSx, p: 3, textAlign: "center" }}>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
            No owner notes available
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
            Notes added for this member will appear here.
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {notes.map((note) => {
            const wasUpdated =
              note.updated_at && note.updated_at !== note.created_at;

            return (
              <Card
                key={note.id}
                sx={{
                  ...pageCardSx,
                  p: { xs: 1.5, sm: 2, md: 2.25 },
                }}
              >
                {editId === note.id ? (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      minRows={4}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      sx={{ ...textFieldSx, mb: 2 }}
                    />

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1.25,
                        flexWrap: "wrap",
                      }}
                    >
                      <Button
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={cancelEdit}
                        sx={secondaryButtonSx}
                      >
                        Cancel
                      </Button>

                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => updateNote(note.id)}
                        disabled={saving}
                        sx={primaryButtonSx}
                      >
                        {saving ? "Saving..." : "Save Changes"}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        whiteSpace: "pre-wrap",
                        color: "#223142",
                        fontSize: "0.98rem",
                        lineHeight: 1.7,
                      }}
                    >
                      {note.note_text}
                    </Typography>

                    <Divider sx={{ my: 2, borderColor: "rgba(47,99,173,0.08)" }} />

                    <Stack spacing={0.75}>
                      <Typography variant="caption" sx={{ color: "#6b778c" }}>
                        <Box component="span" sx={{ fontWeight: 700, color: "#223142" }}>
                          Created:
                        </Box>{" "}
                        {formatDate(note.created_at)}
                        {"  •  "}
                        <Box component="span" sx={{ fontWeight: 700, color: "#223142" }}>
                          By:
                        </Box>{" "}
                        {note.created_by || "Unknown"}
                      </Typography>

                      {wasUpdated && (
                        <Typography variant="caption" sx={{ color: "#6b778c" }}>
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, color: "#223142" }}
                          >
                            Updated:
                          </Box>{" "}
                          {formatDate(note.updated_at)}
                          {"  •  "}
                          <Box
                            component="span"
                            sx={{ fontWeight: 700, color: "#223142" }}
                          >
                            By:
                          </Box>{" "}
                          {note.updated_by || "Unknown"}
                        </Typography>
                      )}
                    </Stack>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 2,
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          setEditId(note.id);
                          setEditText(note.note_text);
                        }}
                        sx={{
                          border: "1px solid rgba(47,99,173,0.12)",
                          borderRadius: "10px",
                          color: "#2f63ad",
                          backgroundColor: "rgba(47,99,173,0.04)",
                          "&:hover": {
                            backgroundColor: "rgba(47,99,173,0.1)",
                          },
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        onClick={() => deleteNote(note.id)}
                        sx={{
                          border: "1px solid rgba(220,38,38,0.12)",
                          borderRadius: "10px",
                          color: "#dc2626",
                          backgroundColor: "rgba(220,38,38,0.04)",
                          "&:hover": {
                            backgroundColor: "rgba(220,38,38,0.1)",
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </>
                )}
              </Card>
            );
          })}
        </Stack>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OwnerNotesTab;